import React from "react";
import { motion } from "framer-motion";
import { X, Mail, Phone, Calendar, CheckCircle, XCircle } from "lucide-react";
import { IUserProps } from "../types/User";
import { IOrganizationProps } from "../types/Organization";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";

interface ProfileModalProps {
  onClose: () => void;
  user: IUserProps;
  organization: IOrganizationProps;
}

export function ProfileModal({
  onClose,
  user,
  organization,
}: ProfileModalProps) {
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#17265a] via-[#1f2f6b] to-[#2d3f7a] p-6 text-white relative">
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </motion.button>

          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20 border-4 border-white/20">
              {user.image ? (
                <AvatarImage src={user.image} alt={`${user.first_name} ${user.last_name}`} />
              ) : null}
              <AvatarFallback className="bg-white/20 text-white text-2xl font-semibold">
                {getInitials(user.first_name, user.last_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-bold mb-1">
                {user.first_name} {user.last_name}
              </h2>
              <p className="text-white/80">{organization.name}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Personal Information
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#17265a]/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#17265a]" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-gray-900 font-medium">{user.email}</p>
                </div>
                {user.email_verified ? (
                  <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 ml-auto" />
                )}
              </div>

              {user.phone_number && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#17265a]/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#17265a]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone Number</p>
                    <p className="text-gray-900 font-medium">
                      {user.phone_number}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#17265a]/10 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#17265a]" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Login</p>
                  <p className="text-gray-900 font-medium">
                    {formatDate(user.last_login)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#17265a]/10 rounded-lg flex items-center justify-center">
                  {user.is_active ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Status</p>
                  <p className="text-gray-900 font-medium">
                    {user.is_active ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Organization Info */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Organization
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-900 font-medium mb-2">
                {organization.name}
              </p>
              {organization.subtitle && (
                <p className="text-sm text-gray-600">{organization.subtitle}</p>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

